// Reçete formundaki "Satış Kanalları" bölümü için saf yardımcılar.
//
// Reçete detayı (GET /api/recipes/{id}) `satis_kanallari: [{kanal_id, kod, ad,
// tip, aktif, satis_fiyati, sira}]` döner. Aktif kanal listesi
// (GET /api/satis-kanallari) ile birleştirilip her aktif kanal bir satır olur;
// reçetede zaten varsa "seçili" işaretlenir. Kayıt sırasında yalnız seçili
// satırlar PUT /api/recipes/{id}/satis-kanallari gövdesine girer.
//
// LATENT-BUG DERSİ (#353): seçim state'i yüklenen veriden BESLENMEZSE, yeniden
// kayıt mevcut atamaları siler. selectionFromRecipe + mergeKanalSecim bunu
// birlikte önler.

/**
 * Reçete detayındaki satis_kanallari -> {kanal_id, kod, ad, tip, aktif, satis_fiyati, sira} dizisi.
 * kod/ad/tip de taşınır: kanal Ayarlar'dan pasifleştirilmiş (aktif kanal listesinde yok)
 * ama reçetede hâlâ atalıysa, formda gösterilebilmesi için display alanları gerekir.
 * @param {object} data - GET /api/recipes/{id} yanıtı
 * @returns {Array<{kanal_id:number, kod:string, ad:string, tip:string, aktif:boolean, satis_fiyati:(number|''), sira:number}>}
 */
export function selectionFromRecipe(data) {
  const list = (data && data.satis_kanallari) || [];
  return list.map(k => ({
    kanal_id: k.kanal_id,
    kod: k.kod,
    ad: k.ad,
    tip: k.tip,
    aktif: k.aktif !== false,
    satis_fiyati: (k.satis_fiyati == null) ? '' : k.satis_fiyati,
    sira: k.sira || 0,
  }));
}

/**
 * Tüm aktif kanallar + reçetenin mevcut seçimini birleştirir → form seçim state'i.
 * Her aktif kanal bir satır; reçetede varsa secili=true ve değerleri ondan gelir.
 *
 * M.6 (sessiz veri kaybı): Reçetede atalı bir kanal Ayarlar'dan pasifleştirilirse
 * (artık aktif kanal listesinde yok), atama yine de korunmalı. Backend PUT'u aktif
 * filtreleme yapmaz; bu yüzden aktif katalogda olmayan ama reçetede atalı her kanal
 * için ek bir satır (secili:true, kanalPasif:true) eklenir → selectionToPayload ile
 * round-trip eder, yeniden kayıtta SİLİNMEZ. Kullanıcı bilinçli olarak işareti
 * kaldırarak atamayı sonlandırabilir.
 *
 * @param {Array<{id:number, ad:string, tip:string}>} tumKanallar - aktif kanallar
 * @param {Array} secim - selectionFromRecipe çıktısı (reçetenin mevcut kanalları)
 * @returns {Array<{kanal_id:number, ad:string, tip:string, secili:boolean, aktif:boolean, satis_fiyati:(number|''), sira:number, kanalPasif?:boolean}>}
 */
export function mergeKanalSecim(tumKanallar, secim) {
  const byId = {};
  (secim || []).forEach(s => { byId[s.kanal_id] = s; });

  const aktifIds = new Set();
  // Aktif katalog satırları (önce).
  const rows = (tumKanallar || []).map(k => {
    aktifIds.add(k.id);
    const mevcut = byId[k.id];
    return {
      kanal_id: k.id,
      ad: k.ad,
      tip: k.tip,
      secili: !!mevcut,
      aktif: mevcut ? mevcut.aktif !== false : true,
      satis_fiyati: mevcut ? mevcut.satis_fiyati : '',
      sira: mevcut ? mevcut.sira : 0,
    };
  });

  // Aktif katalogda OLMAYAN ama reçetede atalı kanallar (pasifleştirilmiş) — sonra.
  (secim || []).forEach(s => {
    if (aktifIds.has(s.kanal_id)) return;
    rows.push({
      kanal_id: s.kanal_id,
      ad: s.ad,
      tip: s.tip,
      secili: true,
      aktif: s.aktif !== false,
      satis_fiyati: (s.satis_fiyati == null) ? '' : s.satis_fiyati,
      sira: s.sira || 0,
      kanalPasif: true,  // kanal seviyesinde soft-delete (Ayarlar'dan pasifleştirilmiş)
    });
  });

  return rows;
}

/**
 * Form seçim state'i -> PUT gövdesi. SADECE seçili (secili) kanallar gönderilir.
 * satis_fiyati: '' / null -> null; aksi halde sayıya çevrilir.
 * @param {Array} selection - mergeKanalSecim çıktısı (kullanıcı düzenlemesinden sonra)
 * @returns {Array<{kanal_id:number, aktif:boolean, satis_fiyati:(number|null), sira:number}>}
 */
export function selectionToPayload(selection) {
  return (selection || [])
    .filter(s => s.secili)
    .map(s => {
      // Boş / null → null. Aksi halde sayıya çevir; yarım giriş ("5." / ".") veya
      // geçersiz değer NaN → null (422'yi önler — backend Optional[float] bekler).
      let fiyat = null;
      if (!(s.satis_fiyati === '' || s.satis_fiyati == null)) {
        const n = Number(s.satis_fiyati);
        fiyat = Number.isFinite(n) ? n : null;
      }
      return {
        kanal_id: s.kanal_id,
        aktif: s.aktif !== false,
        satis_fiyati: fiyat,
        sira: s.sira || 0,
      };
    });
}

/**
 * POS otomatik atama sonucunu lokal kanalSecim'e MERGE et (manuel check'leri KORUR).
 *
 * Backend `POST /satis-kanallari/pos-otomatik` ADDITIVE'dir — yalnız YENİ atadığı
 * kanalları `atanan` listesinde döner. Kullanıcı bu butona basmadan önce başka bir
 * kanalı (örn. Paket Servis) elle işaretlemişse, o local secili=true bayrağı
 * KORUNMALIDIR. Eski implementasyon serverdan reçeteyi yeniden çekip seed'i
 * sıfırlıyor → merge effect kanalSecim'i baştan kuruyor → kullanıcının kaydedilmemiş
 * manuel check'leri SİLİNİYORDU (kanıt: 2026-06-22 kullanıcı raporu).
 *
 * @param {Array} kanalSecim - mevcut form state (mergeKanalSecim çıktısı + kullanıcı düzenlemeleri)
 * @param {Array<{kanal_id:number}>} atanan - backend'in yeni atadığı kanallar (POST yanıtı)
 * @returns {Array} yeni dizi — atanan'daki kanal_id'ler için secili=true; diğer satırlar dokunulmaz
 */
export function applyPosOtomatikResult(kanalSecim, atanan) {
  const list = Array.isArray(kanalSecim) ? kanalSecim : [];
  const addedIds = new Set(
    (Array.isArray(atanan) ? atanan : [])
      .map(a => a && a.kanal_id)
      .filter(id => id != null)
  );
  if (addedIds.size === 0) return list;
  return list.map(k => addedIds.has(k.kanal_id) ? { ...k, secili: true } : k);
}

